import { ReactNode, useMemo, useCallback, Fragment } from 'react';
import Button from '../core/Button';
import { IconArrowLeft, IconArrowRight, IconCheck } from '@/icons';
import { useIntl } from 'react-intl';
import { cn } from '@/helpers/classnames';

export interface Steps {
  step: number;
  title: string;
  view: ReactNode;
}

export interface ImportFileFormStepsProps {
  steps: Steps[];
  currentStep: number;
  nextIsDisabled: boolean;
  onChange: (step: number) => void;
}

export const ImportFileStepper = ({
  steps,
  currentStep,
  nextIsDisabled,
  onChange,
}: ImportFileFormStepsProps) => {
  const { formatMessage } = useIntl();

  const numberOfSteps = steps.length;
  const isLastStep = currentStep === numberOfSteps;

  const changeStep = useCallback(
    (step: number) => {
      onChange(steps[step - 1].step);
    },
    [onChange, steps],
  );

  return (
    <div className="flex w-full flex-col items-center justify-between gap-4 sm:flex-row sm:gap-0">
      <StepButtons
        currentStep={currentStep}
        numberOfSteps={numberOfSteps}
        onClick={changeStep}
      />
      <div className="flex items-center gap-2 sm:justify-end">
        {currentStep !== 1 && (
          <Button
            startIcon={<IconArrowLeft />}
            onClick={() => changeStep(currentStep - 1)}
          >
            {formatMessage({ defaultMessage: 'Tillbaka' })}
          </Button>
        )}
        {!isLastStep && (
          <Button
            variant="filled"
            endIcon={<IconArrowRight />}
            onClick={() => changeStep(currentStep + 1)}
            disabled={nextIsDisabled}
          >
            {formatMessage({ defaultMessage: 'Nästa' })}
          </Button>
        )}
      </div>
    </div>
  );
};

interface StepButtonsProps {
  currentStep: number;
  numberOfSteps: number;
  onClick: (step: number) => void;
}

const StepButtons = ({
  currentStep,
  numberOfSteps,
  onClick,
}: StepButtonsProps) => {
  const { formatMessage } = useIntl();

  const steps = useMemo(() => {
    return Array.from({ length: numberOfSteps }, (_, i) => i + 1);
  }, [numberOfSteps]);

  const disabled = useCallback(
    (step: number) => {
      if (step === currentStep) return true;
      if (step > currentStep) return true;
      return false;
    },
    [currentStep],
  );

  return (
    <nav
      aria-label={formatMessage({ defaultMessage: 'Importsteg' })}
      className="flex items-center gap-1"
    >
      <ol className="flex items-center gap-1">
        {steps.map((step, index) => (
          <Fragment key={step}>
            <li className="flex items-center gap-1">
              <Button
                round
                onClick={() => onClick(step)}
                className={cn(
                  'h-10 w-10 border-primary-600 bg-primary-600 text-contrast-primary hover:border-background-300 hover:text-primary-600 disabled:bg-background-50 disabled:text-text-800',
                  currentStep === step &&
                    'disabled:border-primary-500 disabled:bg-primary-500 disabled:text-contrast-primary',
                )}
                disabled={disabled(step)}
                aria-current={currentStep === step ? 'step' : undefined}
                aria-label={formatMessage(
                  { defaultMessage: 'Steg {step} av {total}' },
                  { step, total: numberOfSteps },
                )}
              >
                {disabled(step) ? (
                  step.toString()
                ) : (
                  <div className="text-contrast-primary">
                    <IconCheck size={20} aria-hidden="true" />
                  </div>
                )}
              </Button>
            </li>
            {index !== steps.length - 1 && (
              <li aria-hidden="true">
                <div
                  className={cn(
                    'h-[2px] w-10 rounded-sm bg-background-300',
                    currentStep > step && 'bg-primary-400',
                  )}
                />
              </li>
            )}
          </Fragment>
        ))}
      </ol>
    </nav>
  );
};
